// profile/PurchasedEvents.jsx
import React, { useMemo } from 'react';
import axios from 'axios';
import EventList from '../EventList';
import useCachedFetch from './hooks/useCachedFetch';


const toComparableDate = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(`${s}T23:59:59`);
  const normalized = s.includes('T') ? s : s.replace(' ', 'T');
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
};

const isCancelledStatus = (status) => {
  const value = String(status || 'none');
  return [
    'cancellation_pending_payment',
    'canceled',
    'refunding',
    'cancellation_completed',
    'cancellation_failed',
  ].includes(value);
};

const PurchasedEvents = ({ user, isOwner }) => {
  const { data, loading, error, refetch } = useCachedFetch(
    `mytickets:${user?.id}`,
    async (signal) => {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/my-tickets`, { signal });
      return res.data;
    },
    { enabled: !!user?.id, ttlMs: 60_000 }
  );

  const tickets = data?.tickets || [];

  const upcomingPurchasedEvents = useMemo(() => {
    const now = new Date();
    const map = new Map();

    for (const t of tickets) {
      const e = t?.event;
      if (!e?.id) continue;
      if (isCancelledStatus(e?.cancellation_status)) continue;

      const base = e.end_date || e.date;
      const end = toComparableDate(base);
      if (!end) continue;

      // ✅ 未来だけ（過去を除外）
      if (end < now) continue;

      if (!map.has(e.id)) {
        map.set(e.id, {
          ...e,
          title: e.title ?? e.name ?? 'イベント名不明',
          myTickets: [], // ✅ ここ重要
          _purchase: {
            lastPurchasedAt: t.purchased_at,
          },
        });
      }

      const ev = map.get(e.id);
      ev.myTickets.push(t); // ✅ チケットをイベントに紐づけ
      // 任意：最新購入日時を更新
      if (t.purchased_at && (!ev._purchase.lastPurchasedAt || t.purchased_at > ev._purchase.lastPurchasedAt)) {
        ev._purchase.lastPurchasedAt = t.purchased_at;
      }
    }

    return Array.from(map.values()).sort((a, b) => {
      const ad = toComparableDate(a.end_date || a.date)?.getTime() ?? 0;
      const bd = toComparableDate(b.end_date || b.date)?.getTime() ?? 0;
      return ad - bd;
    });
  }, [tickets]);

  if (loading) return <div>読み込み中...</div>;
  if (error) return <div>取得に失敗しました。<button onClick={refetch}>再読み込み</button></div>;
  if (!tickets.length) return <div>購入したチケットはありません。</div>;
  if (!upcomingPurchasedEvents.length) return <div>今後の購入済みイベントはありません。</div>;

  return (
    <EventList
      events={upcomingPurchasedEvents}
      isOwner={isOwner}
      profileRole={user?.role}
      allowEdit={false}
      showCheckinButton={true}   // ✅ これでボタン復活
    />
  );
};

export default PurchasedEvents;
